import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, BookOpen, PenLine } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/Footer";
import StorySubmit from "@/components/StorySubmit";
import { ARTICLES, CATEGORIES, fetchPublishedStories } from "@/data/journal";

export default function Journal() {
  const [cat, setCat] = useState("All");
  const [storyOpen, setStoryOpen] = useState(false);
  const [articles, setArticles] = useState(ARTICLES);

  useEffect(() => {
    fetchPublishedStories()
      .then((stories) => setArticles([...stories, ...ARTICLES]))
      .catch(() => {});
  }, []);

  const visible = articles.filter((a) => cat === "All" || a.category === cat);

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <PageHeader />
      <main className="px-6 sm:px-12 lg:px-20 pt-32 sm:pt-40 pb-24 sm:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
            <BookOpen className="w-4 h-4" /> Training &middot; Nutrition &middot; Recovery &middot; Gear &middot; Stories
          </p>
          <h1 data-testid="journal-headline" className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[#0D1117] tracking-tight leading-[1.06]">
            The <span className="italic text-[#FF4E00]">Journal</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#374151] leading-relaxed">
            Field notes from coaches and athletes — training science, fueling, recovery, gear and the
            stories that keep us lacing up.
          </p>
          <Link
            to="/training"
            data-testid="journal-training-hub-link"
            className="group mt-5 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4E00] hover:gap-3.5 transition-all duration-300"
          >
            Want workouts too? Strength &amp; Mobility Hub <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-45" />
          </Link>
        </motion.div>

        <div className="flex flex-wrap gap-2 mb-12" data-testid="journal-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`journal-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              className={`font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2 rounded-full border transition-all duration-300 ${
                cat === c
                  ? "bg-[#FF4E00] border-[#FF4E00] text-[#FBF9F5]"
                  : "border-[#0D1117]/15 text-[#374151] hover:border-[#FF4E00]/50 hover:text-[#FF4E00]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {visible.map((a, i) => (
            <motion.div
              key={a.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: "easeOut" }}
            >
              <Link
                to={`/journal/${a.slug}`}
                data-testid="journal-card"
                className="group block rounded-3xl border border-[#0D1117]/10 hover:border-[#FF4E00]/40 bg-[#FFFFFF] overflow-hidden transition-all duration-300 hover:-translate-y-1"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={a.img} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                </div>
                <div className="p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4E00]">
                    {a.author ? `By ${a.author} · ` : ""}{a.category} · {a.read} read
                  </p>
                  <h2 className="font-serif text-xl sm:text-2xl text-[#0D1117] mt-3 group-hover:text-[#FF4E00] transition-colors leading-snug">
                    {a.title}
                  </h2>
                  <p className="text-sm text-[#374151] leading-relaxed mt-3">{a.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4E00]">
                    Read <ArrowUpRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mt-16 rounded-3xl bg-[#0D1117] p-10 sm:p-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
          data-testid="journal-share-cta"
        >
          <div>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#FBF9F5] tracking-tight">
              Lived a mile worth <span className="italic text-[#FF4E00]">telling?</span>
            </h2>
            <p className="text-sm text-[#FBF9F5]/70 mt-3 max-w-md leading-relaxed">
              Share your training breakthroughs, race-day lessons or recovery wins — photo included — and inspire the tribe.
            </p>
          </div>
          <button
            onClick={() => setStoryOpen(true)}
            data-testid="journal-share-button"
            className="shrink-0 inline-flex items-center gap-2 bg-[#FF4E00] hover:bg-[#E04000] text-[#FBF9F5] font-mono text-[11px] uppercase tracking-[0.2em] px-7 py-3.5 rounded-full transition-colors duration-300"
          >
            <PenLine className="w-4 h-4" /> Share Your Story
          </button>
        </motion.div>
      </main>
      <StorySubmit open={storyOpen} onClose={() => setStoryOpen(false)} />
      <Footer />
    </div>
  );
}
